import React, { useState } from 'react';
import { 
  WifiOff, 
  QrCode, 
  Copy, 
  Check, 
  Eye, 
  EyeOff, 
  Settings, 
  LogOut, 
  PlusCircle, 
  BookOpen,
  Smartphone,
  ShieldCheck,
  Zap
} from 'lucide-react';
import { ConnectionStatus } from '../types';
import { CkLogo } from './CkLogo';
import { getAppInviteUrl } from '../utils/urlHelper';

interface RoomHeaderProps {
  roomId: string;
  accessCode: string;
  status: ConnectionStatus;
  peerCount: number;
  onOpenQr: () => void;
  onOpenFlow: () => void;
  onOpenSettings: () => void;
  onOpenDownloads: () => void;
  onOpenGuide: (tab?: 'android' | 'windows' | 'security') => void;
  onDisconnect: () => void;
  onNewRoom: () => void;
}

export const RoomHeader: React.FC<RoomHeaderProps> = ({
  roomId,
  accessCode,
  status,
  peerCount,
  onOpenQr,
  onOpenFlow,
  onOpenSettings,
  onOpenDownloads,
  onOpenGuide,
  onDisconnect,
  onNewRoom
}) => {
  const [showAccessCode, setShowAccessCode] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedRoom, setCopiedRoom] = useState(false);

  const handleCopyLink = () => {
    const url = getAppInviteUrl(roomId, accessCode);
    navigator.clipboard.writeText(url);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const handleCopyRoom = () => {
    navigator.clipboard.writeText(roomId);
    setCopiedRoom(true);
    setTimeout(() => setCopiedRoom(false), 2000);
  };

  const getStatusBadge = () => {
    switch (status) {
      case 'connected':
        return (
          <div className="flex items-center gap-2 bg-emerald-950/40 border border-emerald-500/30 text-emerald-400 px-2.5 py-1 rounded-full text-xs font-bold shadow-inner">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
            </span>
            <span>Live Mesh • {peerCount} {peerCount === 1 ? 'Peer' : 'Peers'}</span>
          </div>
        );
      case 'connecting':
      case 'reconnecting':
        return (
          <div className="flex items-center gap-2 bg-amber-950/40 border border-amber-500/30 text-amber-400 px-2.5 py-1 rounded-full text-xs font-bold">
            <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse"></span>
            <span>Syncing...</span>
          </div>
        );
      default:
        return (
          <div className="flex items-center gap-2 bg-rose-950/40 border border-rose-500/30 text-rose-400 px-2.5 py-1 rounded-full text-xs font-bold">
            <WifiOff size={12} />
            <span>Disconnected</span>
          </div>
        );
    }
  };

  return (
    <header className="native-chrome native-glass rounded-2xl p-3.5 md:p-4 mb-5 border border-white/10 relative overflow-hidden">
      {/* Top Accent Gradient Line */}
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-cyan-400 via-purple-500 to-emerald-400 opacity-80"></div>

      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3.5">
        
        {/* Left: Native Brand & Status Pill */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            {/* macOS traffic light aesthetic dot indicators */}
            <div className="hidden sm:flex items-center gap-1.5 mr-1 opacity-70">
              <span className="w-2.5 h-2.5 rounded-full bg-rose-500/80"></span>
              <span className="w-2.5 h-2.5 rounded-full bg-amber-500/80"></span>
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/80"></span>
            </div>

            <CkLogo size={36} className="shadow-lg shadow-cyan-500/20" />
            
            <div>
              <div className="flex items-center gap-2">
                <span className="text-base md:text-lg font-black tracking-tight text-white font-display">
                  CK <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-400">Share</span>
                </span>
                <span className="px-1.5 py-0.2 text-[9px] font-mono font-bold text-cyan-300 bg-cyan-500/10 border border-cyan-500/30 rounded-md">
                  PRO
                </span>
              </div>
              <p className="text-[10px] text-slate-400 font-mono hidden md:block">Universal E2EE Relay Mesh</p>
            </div>
          </div>

          <div className="h-6 w-[1px] bg-white/10 hidden sm:block mx-1"></div>

          {/* Connection Status Indicator */}
          {getStatusBadge()}
        </div>

        {/* Center: Room Code & Access Key Pill */}
        {roomId ? (
          <div className="flex items-center gap-2 bg-[#080A14] px-3.5 py-1.5 rounded-xl border border-white/10 shadow-inner text-xs">
            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Room:</span>
            <button
              onClick={handleCopyRoom}
              className="font-mono font-bold text-cyan-300 hover:text-cyan-200 flex items-center gap-1.5 transition-colors cursor-pointer"
              title="Click to copy Room Code"
            >
              <span>{roomId}</span>
              {copiedRoom ? <Check size={12} className="text-emerald-400" /> : <Copy size={11} className="opacity-60 hover:opacity-100" />}
            </button>

            <span className="text-slate-700 mx-1">|</span>

            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Key:</span>
            <span className="font-mono text-slate-300 flex items-center gap-1.5">
              {showAccessCode ? accessCode : '••••••••'}
              <button
                onClick={() => setShowAccessCode(!showAccessCode)}
                className="text-slate-400 hover:text-cyan-400 transition-colors p-0.5 cursor-pointer"
                title={showAccessCode ? 'Hide Access Key' : 'Show Access Key'}
              >
                {showAccessCode ? <EyeOff size={12} /> : <Eye size={12} />}
              </button>
            </span>
          </div>
        ) : null}

        {/* Right: Quick Action Controls */}
        <div className="flex items-center gap-2 flex-wrap">
          {roomId ? (
            <>
              {/* Pair Phone QR Button */}
              <button
                onClick={onOpenQr}
                className="btn-secondary text-xs py-1.5 px-3 flex items-center gap-1.5 cursor-pointer"
                title="Pair Phone via QR Code"
              >
                <QrCode size={14} className="text-cyan-400" />
                <span className="hidden sm:inline">Pair Phone</span>
              </button>

              {/* CK Flow (Multi-PC Mouse Sharing) */}
              <button
                onClick={onOpenFlow}
                className="btn-secondary text-xs py-1.5 px-3 flex items-center gap-1.5 text-cyan-300 border-cyan-500/30 hover:border-cyan-400 cursor-pointer shadow-[0_0_12px_rgba(0,240,255,0.15)]"
                title="CK Flow: Multi-PC Mouse & Keyboard Mesh"
              >
                <Zap size={14} className="text-cyan-400" />
                <span className="font-bold">CK Flow</span>
              </button>

              {/* Copy Invite Link */}
              <button
                onClick={handleCopyLink}
                className="btn-secondary text-xs py-1.5 px-3 flex items-center gap-1.5 cursor-pointer"
                title="Copy Direct Invite Link"
              >
                {copiedLink ? (
                  <>
                    <Check size={14} className="text-emerald-400" />
                    <span className="text-emerald-400 font-bold">Copied</span>
                  </>
                ) : (
                  <>
                    <Copy size={14} className="text-slate-400" />
                    <span className="hidden sm:inline">Invite Link</span>
                  </>
                )}
              </button>

              {/* Guide */}
              <button
                onClick={() => onOpenGuide('android')}
                className="btn-secondary text-xs py-1.5 px-3 flex items-center gap-1.5 cursor-pointer"
                title="Setup Guide"
              >
                <BookOpen size={14} className="text-emerald-400" />
                <span className="hidden md:inline">Guide</span>
              </button>

              {/* Get Apps / Downloads */}
              <button
                onClick={onOpenDownloads}
                className="btn-secondary text-xs py-1.5 px-3 text-cyan-300 border-cyan-500/30 hover:border-cyan-400 flex items-center gap-1.5 cursor-pointer"
                title="Download Android APK, Windows Tray App, Extension"
              >
                <Smartphone size={14} className="text-cyan-400" />
                <span className="hidden md:inline">Get Apps</span>
              </button>

              {/* Settings */}
              <button
                onClick={onOpenSettings}
                className="btn-icon"
                title="Sync & Engine Preferences"
              >
                <Settings size={16} />
              </button>

              {/* Leave / Disconnect */}
              <button
                onClick={onDisconnect}
                className="btn-icon hover:text-rose-400 hover:border-rose-500/40 hover:bg-rose-950/30"
                title="Disconnect & Exit Room"
              >
                <LogOut size={16} />
              </button>
            </>
          ) : (
            <div className="flex items-center gap-2">
              <button
                onClick={onOpenDownloads}
                className="btn-secondary text-xs py-1.5 px-3 text-cyan-300 border-cyan-500/30 hover:border-cyan-400 flex items-center gap-1.5 cursor-pointer"
                title="Get Native Apps"
              >
                <Smartphone size={14} className="text-cyan-400" />
                <span>Get Apps</span>
              </button>
              <button onClick={onNewRoom} className="btn-primary text-xs py-1.5 px-3.5">
                <PlusCircle size={15} />
                <span>Create New Room</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};
